import EntityStatus from "../components/EntityStatus";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import GenericTable, { GenericTableColumn } from "../components/GenericTable";
import PageSectionCard from "../components/PageSectionCard";
import Pagination from "../components/Pagination";
import { useAppSession } from "../context/AppSessionContext";
import { fetchAllClientCompanies } from "../lib/clientCompanies";
import { readErrorMessage } from "../lib/http";
import { formatReasonCode, reasonCodeOptions } from "../lib/intakeForm";
import { canExportReports } from "../types/auth";
import {
  ClientCompany,
  Workstation,
  WorkstationTag,
} from "../types/clientCompanies";
import { getEmployeeClientCompany } from "../types/employees";
import { EpisodeListItem } from "../types/incapacity";
import { PaginatedResponse } from "../types/pagination";

const defaultPageSize = 20;
const pageSizeOptions = [10, 20, 50];

type EpisodeStatusFilter = "" | "ongoing" | "upcoming" | "ended";
type EpisodeWindowStatus = "Ongoing" | "Upcoming" | "Ended";

const toLocalDateKey = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toDateKeyFromIso = (value: string | null) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return toLocalDateKey(parsed);
};

const formatDate = (value: string | null) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(parsed);
};

const formatDateTime = (value: string | null) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const getEpisodeWindowStatus = (
  episode: EpisodeListItem,
  todayKey: string,
): EpisodeWindowStatus => {
  const startDateKey = toDateKeyFromIso(episode.startDate);
  const returnDateKey = toDateKeyFromIso(episode.returnDate);

  if (startDateKey && startDateKey > todayKey) {
    return "Upcoming";
  }

  if (startDateKey && startDateKey <= todayKey) {
    if (!returnDateKey || returnDateKey >= todayKey) {
      return "Ongoing";
    }
  }

  return "Ended";
};

const statusBadgeClassName = (status: EpisodeWindowStatus) => {
  if (status === "Upcoming") {
    return "statusBadgeUpcoming";
  }

  if (status === "Ended") {
    return "statusBadgeEnded";
  }

  return "statusBadgeOngoing";
};

function ReportsPage() {
  const { t } = useTranslation();
  const { authorizedFetch, me } = useAppSession();
  const canExport = canExportReports(me?.roles ?? []);

  const [episodes, setEpisodes] = useState<EpisodeListItem[]>([]);
  const [companies, setCompanies] = useState<ClientCompany[]>([]);
  const [isLoadingEpisodes, setIsLoadingEpisodes] = useState(true);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [episodesError, setEpisodesError] = useState<string | null>(null);
  const [companiesError, setCompaniesError] = useState<string | null>(null);

  const [clientCompanyId, setClientCompanyId] = useState("");
  const [tagId, setTagId] = useState("");
  const [workstationId, setWorkstationId] = useState("");
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [tags, setTags] = useState<WorkstationTag[]>([]);
  const [workstationsError, setWorkstationsError] = useState<string | null>(
    null,
  );
  const [loadingWorkstations, setLoadingWorkstations] = useState(false);
  const [reasonCode, setReasonCode] = useState("");
  const [status, setStatus] = useState<EpisodeStatusFilter>("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultPageSize);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // The table and the PDF export send the same filters, so the export always
  // contains exactly the episodes the report shows.
  const reportFilters = useMemo(() => {
    const filters: Record<string, string> = {};

    if (clientCompanyId) filters.clientCompanyId = clientCompanyId;
    if (tagId) filters.tagId = tagId;
    if (workstationId) filters.workstationId = workstationId;
    if (reasonCode) filters.reasonCode = reasonCode;
    if (status) filters.status = status;
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;

    return filters;
  }, [
    clientCompanyId,
    tagId,
    workstationId,
    reasonCode,
    status,
    dateFrom,
    dateTo,
  ]);

  const loadEpisodes = useCallback(async () => {
    setIsLoadingEpisodes(true);
    setEpisodesError(null);

    const query = new URLSearchParams({
      ...reportFilters,
      page: String(page),
      limit: String(limit),
    });

    try {
      const response = await authorizedFetch(`/episodes?${query.toString()}`);

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            t("Failed to load report episodes ({{status}}).", {
              status: response.status,
            }),
          ),
        );
      }

      const payload =
        (await response.json()) as PaginatedResponse<EpisodeListItem>;
      const responseTotalPages = payload.totalPages ?? 0;

      if (responseTotalPages > 0 && page > responseTotalPages) {
        setPage(responseTotalPages);
        return;
      }

      setEpisodes(payload.data ?? []);
      setTotal(payload.total ?? 0);
      setTotalPages(responseTotalPages);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("Failed to load report episodes.");
      setEpisodesError(message);
      setEpisodes([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setIsLoadingEpisodes(false);
    }
  }, [authorizedFetch, limit, page, reportFilters, t]);

  const onExport = async () => {
    setIsExporting(true);
    setExportError(null);

    try {
      const response = await authorizedFetch("/reports/exports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reportFilters),
      });

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            t("Failed to create export ({{status}}).", {
              status: response.status,
            }),
          ),
        );
      }

      // The server streams the PDF back and keeps no copy of it. Anything else
      // (e.g. an older backend answering with JSON) must not be saved as a PDF.
      if (!response.headers.get("Content-Type")?.includes("application/pdf")) {
        throw new Error(t("The server did not return a PDF."));
      }

      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = `fjarvistaskyrsla-${toLocalDateKey(new Date())}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(
        error instanceof Error ? error.message : t("Failed to export report."),
      );
    } finally {
      setIsExporting(false);
    }
  };

  const loadCompanies = useCallback(async () => {
    setIsLoadingCompanies(true);
    setCompaniesError(null);

    try {
      setCompanies(await fetchAllClientCompanies(authorizedFetch));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("Failed to load client companies.");
      setCompaniesError(message);
      setCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [authorizedFetch, t]);

  useEffect(() => {
    void loadEpisodes();
  }, [loadEpisodes]);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    let cancelled = false;
    setWorkstations([]);
    setTags([]);
    setWorkstationsError(null);
    setLoadingWorkstations(true);
    const load = async () => {
      try {
        const selected = clientCompanyId
          ? companies.filter((company) => company.id === clientCompanyId)
          : companies;
        const fetchList = async <T,>(path: string) => {
          const response = await authorizedFetch(path);
          if (!response.ok)
            throw new Error(
              await readErrorMessage(
                response,
                t("Failed to load workstations."),
              ),
            );
          return (await response.json()) as T[];
        };
        const [workstationGroups, tagGroups] = await Promise.all([
          Promise.all(
            selected.map((company) =>
              fetchList<Workstation>(
                `/client-companies/${company.id}/workstations`,
              ),
            ),
          ),
          Promise.all(
            selected.map((company) =>
              fetchList<WorkstationTag>(`/client-companies/${company.id}/tags`),
            ),
          ),
        ]);
        if (!cancelled) {
          setWorkstations(workstationGroups.flat());
          setTags(tagGroups.flat());
        }
      } catch (error) {
        if (!cancelled)
          setWorkstationsError(
            error instanceof Error
              ? error.message
              : t("Failed to load workstations."),
          );
      } finally {
        if (!cancelled) setLoadingWorkstations(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, clientCompanyId, companies, t]);

  const filteredWorkstations = useMemo(
    () =>
      tagId
        ? workstations.filter((workstation) =>
            workstation.tags?.some((tag) => tag.id === tagId),
          )
        : workstations,
    [tagId, workstations],
  );

  const companyName = (id: string) =>
    companies.find((company) => company.id === id)?.name ?? "";

  const onFilterChange = (
    nextValue: string,
    setter: (value: string) => void,
  ) => {
    setter(nextValue);
    setPage(1);
  };

  const onLimitChange = (value: string) => {
    const nextLimit = Number.parseInt(value, 10);
    setLimit(Number.isNaN(nextLimit) ? defaultPageSize : nextLimit);
    setPage(1);
  };

  const todayKey = toLocalDateKey(new Date());
  const columns = useMemo<GenericTableColumn<EpisodeListItem>[]>(
    () => [
      {
        id: "employee",
        header: t("Employee"),
        cell: (episode) => (
          <>
            {episode.employee.fullName}{" "}
            <EntityStatus deletedAt={episode.employee.deletedAt} />
          </>
        ),
      },
      {
        id: "clientCompany",
        header: t("Client Company"),
        cell: (episode) => (
          <>
            {getEmployeeClientCompany(episode.employee)?.name ?? "-"}{" "}
            <EntityStatus
              deletedAt={getEmployeeClientCompany(episode.employee)?.deletedAt}
            />
          </>
        ),
      },
      {
        id: "workstation",
        header: t("Workstation"),
        cell: (episode) => (
          <>
            {episode.employee.workstation?.name ?? "-"}{" "}
            <EntityStatus deletedAt={episode.employee.workstation?.deletedAt} />
          </>
        ),
      },
      {
        id: "reportedAt",
        header: t("Reported at"),
        cell: (episode) => formatDateTime(episode.reportedAt),
      },
      {
        id: "startDate",
        header: t("Start date"),
        cell: (episode) => formatDate(episode.startDate),
      },
      {
        id: "estimatedReturnDate",
        header: t("Est. return"),
        cell: (episode) => formatDate(episode.estimatedReturnDate),
      },
      {
        id: "returnDate",
        header: t("Return date"),
        cell: (episode) => formatDate(episode.returnDate),
      },
      {
        id: "daysAway",
        header: t("Days away"),
        cell: (episode) => episode.daysAway ?? "-",
      },
      {
        id: "reasonCode",
        header: t("Reason"),
        cell: (episode) => t(formatReasonCode(episode.reasonCode)),
      },
      {
        id: "status",
        header: t("Status"),
        cell: (episode) => {
          const windowStatus = getEpisodeWindowStatus(episode, todayKey);
          return (
            <span
              className={`statusBadge ${statusBadgeClassName(windowStatus)}`}
            >
              {t(windowStatus)}
            </span>
          );
        },
      },
      {
        id: "musculoskeletal",
        header: t("Musculoskeletal issue"),
        cell: (episode) => (episode.musculoskeletal ? t("Yes") : t("No")),
      },
      {
        id: "verified",
        header: t("Incapacity certified by Akkilles"),
        cell: (episode) => (episode.verified ? t("Yes") : t("No")),
      },
      {
        id: "actions",
        header: t("Actions"),
        headerClassName: "actionsColumn",
        cellClassName: "actionsColumn",
        cell: (episode) => (
          <Link
            className="secondaryButton tableActionButton"
            to={`/episodes/${episode.id}`}
          >
            {t("Edit")}
          </Link>
        ),
      },
    ],
    [t, todayKey],
  );

  const companySelectId = "reports-company-filter";
  const pageSizeSelectId = "reports-page-size";
  const reasonSelectId = "reports-reason-filter";
  const statusSelectId = "reports-status-filter";
  const fromDateId = "reports-date-from";
  const toDateId = "reports-date-to";

  const selectedCompanyLabel = useMemo(
    () =>
      companies.find((company) => company.id === clientCompanyId)?.name ??
      t("all companies"),
    [clientCompanyId, companies, t],
  );

  const showEmptyState =
    !isLoadingEpisodes && !episodesError && episodes.length === 0;

  return (
    <PageSectionCard
      titleId="reports-title"
      eyebrow={t("Reports")}
      title={t("Episodes report")}
      lead={t(
        "Browse episodes with server-side filters across companies and date windows.",
      )}
      actions={
        canExport ? (
          <div className="headerActions">
            <button
              className="primaryButton"
              type="button"
              onClick={() => void onExport()}
              disabled={isExporting || !clientCompanyId}
              title={
                clientCompanyId
                  ? undefined
                  : t("Select a client company to export")
              }
            >
              {isExporting ? t("Exporting...") : t("Export PDF")}
            </button>
          </div>
        ) : undefined
      }
    >
      <div
        className="listControls"
        role="group"
        aria-label={t("Episode filters")}
      >
        <label className="field" htmlFor={companySelectId}>
          <span className="fieldLabel">{t("Client company")}</span>
          <select
            className="inputControl"
            id={companySelectId}
            value={clientCompanyId}
            onChange={(event) => {
              setTagId("");
              setWorkstationId("");
              onFilterChange(event.target.value, setClientCompanyId);
            }}
            disabled={isLoadingCompanies}
          >
            <option value="">{t("All companies")}</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field" htmlFor="reports-tag-filter">
          <span className="fieldLabel">{t("Tag")}</span>
          <select
            className="inputControl"
            id="reports-tag-filter"
            value={tagId}
            disabled={loadingWorkstations || !!workstationsError}
            onChange={(event) => {
              const nextTagId = event.target.value;
              const keepsWorkstation =
                !nextTagId ||
                workstations
                  .find((workstation) => workstation.id === workstationId)
                  ?.tags?.some((tag) => tag.id === nextTagId);
              if (!keepsWorkstation) setWorkstationId("");
              onFilterChange(nextTagId, setTagId);
            }}
          >
            <option value="">{t("All tags")}</option>
            {tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {clientCompanyId
                  ? tag.name
                  : `${companyName(tag.clientCompanyId)} — ${tag.name}`}
              </option>
            ))}
          </select>
        </label>

        <label className="field" htmlFor="reports-workstation-filter">
          <span className="fieldLabel">{t("Workstation")}</span>
          <select
            className="inputControl"
            id="reports-workstation-filter"
            value={workstationId}
            disabled={loadingWorkstations || !!workstationsError}
            onChange={(event) =>
              onFilterChange(event.target.value, setWorkstationId)
            }
          >
            <option value="">{t("All workstations")}</option>
            {filteredWorkstations.map((workstation) => (
              <option key={workstation.id} value={workstation.id}>
                {clientCompanyId
                  ? workstation.name
                  : `${companyName(workstation.clientCompanyId)} — ${workstation.name}`}
              </option>
            ))}
          </select>
        </label>
        {workstationsError ? (
          <p className="errorMessage" role="alert">
            {workstationsError}
          </p>
        ) : null}

        <label className="field" htmlFor={fromDateId}>
          <span className="fieldLabel">{t("From")}</span>
          <input
            className="inputControl"
            id={fromDateId}
            type="date"
            value={dateFrom}
            onChange={(event) =>
              onFilterChange(event.target.value, setDateFrom)
            }
          />
        </label>

        <label className="field" htmlFor={toDateId}>
          <span className="fieldLabel">{t("To")}</span>
          <input
            className="inputControl"
            id={toDateId}
            type="date"
            value={dateTo}
            onChange={(event) => onFilterChange(event.target.value, setDateTo)}
          />
        </label>

        <label className="field" htmlFor={reasonSelectId}>
          <span className="fieldLabel">{t("Reason")}</span>
          <select
            className="inputControl"
            id={reasonSelectId}
            value={reasonCode}
            onChange={(event) =>
              onFilterChange(event.target.value, setReasonCode)
            }
          >
            <option value="">{t("All reasons")}</option>
            {reasonCodeOptions.map((option) => (
              <option key={option} value={option}>
                {t(formatReasonCode(option))}
              </option>
            ))}
          </select>
        </label>

        <label className="field" htmlFor={statusSelectId}>
          <span className="fieldLabel">{t("Status")}</span>
          <select
            className="inputControl"
            id={statusSelectId}
            value={status}
            onChange={(event) =>
              onFilterChange(event.target.value, (value) =>
                setStatus(value as EpisodeStatusFilter),
              )
            }
          >
            <option value="">{t("All statuses")}</option>
            <option value="ongoing">{t("Ongoing")}</option>
            <option value="upcoming">{t("Upcoming")}</option>
            <option value="ended">{t("Ended")}</option>
          </select>
        </label>

        <label className="field" htmlFor={pageSizeSelectId}>
          <span className="fieldLabel">{t("Page size")}</span>
          <select
            className="inputControl"
            id={pageSizeSelectId}
            value={String(limit)}
            onChange={(event) => onLimitChange(event.target.value)}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="hintText">
        {t(
          "Dates include episodes where the employee was on leave at any point in the period, and episodes reported during the period.",
        )}
      </p>

      {exportError ? (
        <p className="errorMessage" role="alert">
          {exportError}
        </p>
      ) : null}

      {isLoadingEpisodes ? <p>{t("Loading episodes...")}</p> : null}

      {episodesError ? (
        <p className="errorMessage" role="alert">
          {episodesError}
        </p>
      ) : null}

      {companiesError ? (
        <p className="errorMessage" role="alert">
          {t("Unable to load client-company filter options: {{error}}", {
            error: companiesError,
          })}
        </p>
      ) : null}

      {showEmptyState ? (
        <div className="emptyState">
          <p>
            {t("No episodes found for {{company}}.", {
              company: selectedCompanyLabel,
            })}
          </p>
        </div>
      ) : null}

      {!isLoadingEpisodes && !episodesError && episodes.length > 0 ? (
        <GenericTable
          ariaLabel={t("Episodes report table")}
          columns={columns}
          rows={episodes}
          rowKey={(episode) => episode.id}
        />
      ) : null}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={isLoadingEpisodes}
        summary={
          total === 1
            ? t("{{count}} episode", { count: total })
            : t("{{count}} episodes", { count: total })
        }
      />
    </PageSectionCard>
  );
}

export default ReportsPage;
